import { Guid, Nillable } from '@web/utils';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { AdminMultimediaImageUpload } from '@web/shared/module/multimedia';
import { AdminProductStatus } from '../enums/admin-product-status';
import { Nullable } from 'primeng/ts-helpers';

export class AdminProductAttributes {
  public constructor(
    private readonly data: {
      items: Array<AdminProductAttributeItem>;
    },
  ) {}

  public get items(): Array<AdminProductAttributeItem> {
    return this.data.items;
  }
}

export type AdminProductAttributeItem = {
  content: string;
  id?: Nillable<Guid>;
  productAttributeId: Nillable<Guid>;
};
export type AdminProductAttributeFormGroup = FormGroup<{
  content: FormControl<string>;
  id: FormControl<Nillable<Guid>>;
  productAttributeId: FormControl<Nillable<Guid>>;
}>;
export type AdminProductAttributesFormGroup = FormGroup<{
  attributes: FormArray<AdminProductAttributeFormGroup>;
}>;

export class AdminProductCharacteristics {
  public constructor(
    private readonly data: {
      items: Array<AdminProductCharacteristicItem>;
    },
  ) {}

  public get items(): Array<AdminProductCharacteristicItem> {
    return this.data.items;
  }
}

export type AdminProductCharacteristicItem = {
  content: string;
  id?: Nillable<Guid>;
  label: string;
};
export type AdminProductCharacteristicFormGroup = FormGroup<{
  content: FormControl<string>;
  id: FormControl<Nillable<Guid>>;
  label: FormControl<string>;
}>;
export type AdminProductCharacteristicsFormGroup = FormGroup<{
  characteristics: FormArray<AdminProductCharacteristicFormGroup>;
}>;

export class AdminProductDetailsForm {
  public static PriceMin = 0;
  public static FilesMinCount = 1;
  public static MinMaxQuantityPerOrder = 1;
  public static MinMaxAvailableQuantity = 0;
  public static NameMaxLength = 255;
  public static PasswordMaxLength = 255;

  public constructor(
    private readonly data: {
      attributes?: Nillable<AdminProductAttributes>;
      characteristics?: Nillable<AdminProductCharacteristics>;
      cosmeticBrandId?: Nillable<Guid>;
      featuredFile?: Nillable<AdminMultimediaImageUpload>;
      files?: Nillable<AdminMultimediaImageUpload[]>;
      name: string;
      password?: Nillable<string>;
      price?: Nillable<number>;
      slug?: Nillable<string>;
      status: AdminProductStatus;
      virtual?: Nillable<boolean>;
      maxQuantityPerOrder?: Nillable<number>;
      availableQuantity?: Nillable<number>;
      warehouseProductId?: Nillable<Guid>;
      canBeWarehouseProductIdEdited?: Nillable<boolean>;
      id?: Nillable<Guid>;
    },
  ) {}

  public get name(): string {
    return this.data.name;
  }

  public get password(): Nillable<string> {
    return this.data.password;
  }

  public get slug(): Nillable<string> {
    return this.data.slug;
  }

  public get cosmeticBrandId(): Nillable<Guid> {
    return this.data.cosmeticBrandId;
  }

  public get warehouseProductId(): Nillable<Guid> {
    return this.data.warehouseProductId;
  }

  public get featuredFile(): Nillable<AdminMultimediaImageUpload> {
    return this.data.featuredFile;
  }

  public get files(): Nillable<AdminMultimediaImageUpload[]> {
    return this.data.files;
  }

  public get characteristics(): Nillable<AdminProductCharacteristics> {
    return this.data.characteristics;
  }

  public get attributes(): Nillable<AdminProductAttributes> {
    return this.data.attributes;
  }

  public get price(): Nillable<number> {
    return this.data.price;
  }

  public get status(): AdminProductStatus {
    return this.data.status;
  }

  public get virtual(): Nillable<boolean> {
    return this.data.virtual;
  }

  public get maxQuantityPerOrder(): Nillable<number> {
    return this.data.maxQuantityPerOrder;
  }

  public get availableQuantity(): Nillable<number> {
    return this.data.availableQuantity;
  }

  public get canBeWarehouseProductIdEdited(): Nillable<boolean> {
    return this.data.canBeWarehouseProductIdEdited;
  }

  public get id(): Nillable<Guid> {
    return this.data.id;
  }
}

export type AdminProductDetailsFormGroup = FormGroup<{
  attributes: FormControl<Nillable<AdminProductAttributes>>;
  characteristics: FormControl<Nillable<AdminProductCharacteristics>>;
  cosmeticBrandId: FormControl<Nillable<Guid>>;
  featuredFile: FormControl<Nillable<AdminMultimediaImageUpload>>;
  files: FormControl<Nillable<AdminMultimediaImageUpload[]>>;
  name: FormControl<string>;
  password: FormControl<Nillable<string>>;
  price: FormControl<Nullable<number>>;
  slug: FormControl<Nillable<string>>;
  status: FormControl<Nillable<AdminProductStatus>>;
  virtual: FormControl<Nillable<boolean>>;
  maxQuantityPerOrder: FormControl<Nillable<number>>;
  availableQuantity: FormControl<Nillable<number>>;
  warehouseProductId: FormControl<Nillable<Guid>>;
}>;
